import { useCallback, useMemo } from "react";
import type { FilledCondition, SegmentRules } from "./CreateSegment.Constants";
import { isFilledCondition } from "./CreateSegment.Utils";
import { usePredictedListLabel, useSegmentFormatters, useSegmentLabel } from "./UseSegmentLabel";

export const useSegmentSummary = (rules: SegmentRules, enabled: boolean) => {
  const label = useSegmentLabel();
  const listLabel = usePredictedListLabel();
  const { formatValue, formatDate, formatList } = useSegmentFormatters();

  const describeOrderActivity = useCallback(
    (condition: Extract<FilledCondition, { category: "orderActivity" }>) => {
      const { action, comparison, count, periodValue, periodUnit } = condition;
      const activity =
        action === "placedOrder"
          ? label("summary.orderActivity.placedOrder", {
              comparison: label(`fields.orderActivity.comparisons.${comparison}`),
              count: count ?? 0,
              value: formatValue(count ?? 0),
            })
          : label("summary.orderActivity.notPlacedOrder");
      if (!periodValue) {
        return activity;
      }
      const period = label(`summary.units.${periodUnit}`, {
        count: periodValue,
        value: formatValue(periodValue),
      });
      return label("summary.orderActivity.withPeriod", { activity, period });
    },
    [label, formatValue]
  );

  const describeCondition = useCallback(
    (condition: FilledCondition): string => {
      switch (condition.category) {
        case "list":
          return label(`summary.list.${condition.membership}`, {
            list: condition.listName ? listLabel(condition.listName) : "",
          });
        case "location":
          return label(`summary.location.${condition.operator}`, {
            values: formatList(condition.values),
          });
        case "orderActivity":
          return describeOrderActivity(condition);
        case "signupDate":
          return label(`summary.signupDate.${condition.operator}`, {
            date: condition.date ? formatDate(condition.date) : "",
          });
      }
    },
    [label, listLabel, formatList, formatDate, describeOrderActivity]
  );

  return useMemo(() => {
    if (!enabled) {
      return "";
    }
    const clauses = rules.conditions.map((condition, index) => {
      const rule = isFilledCondition(condition) ? describeCondition(condition) : "";
      return index === 0
        ? label("summary.start", { rule })
        : label(`summary.${rules.operators[index - 1] ?? "and"}`, { rule });
    });
    return `${clauses.join("")}${label("summary.end")}`;
  }, [enabled, rules, label, describeCondition]);
};
